import { CommonDialogs } from "@mcleod/common";
import { BlurEvent, ChangeEvent, DataSourceMode, DataSourceModeChangeEvent, LookupModelSearchEvent, LookupModelSelectionEvent, Textbox } from "@mcleod/components";
import { getThemeColor, StringUtil } from "@mcleod/core";
import { DuplicateId } from "../DuplicateId";
import { AutogenLayoutMobileCommControlGeneral } from "./autogen/AutogenLayoutMobileCommControlGeneral";
import { MobileCommValidationUtils, VALIDATION_CONFIG } from "./MobileCommValidationUtils";

export class MobileCommControlGeneral extends AutogenLayoutMobileCommControlGeneral {

    private static readonly SYMPHONY_VENDOR_PREFIX = "SYM";

    async onLoad() {
        try {
            this.setupEventListeners();
            this.setupUIEventListeners();
            this.addGeneralLayoutLoadListener();
        } catch (error) {
            console.error("Error loading MobileCommControlGeneral:", error);
        }
    }

    /**
     * Hides all switches when the vendor ID starts with 'SYM', otherwise shows them.
     */
    public updateSwitchesVisibility(): void {
        const vendorId = this.textboxVendorId?.text?.trim();
        const isSymphonyVendor = vendorId?.toUpperCase().startsWith(MobileCommControlGeneral.SYMPHONY_VENDOR_PREFIX) ?? false;
        const visible = !isSymphonyVendor;

        this.switchCommunications.visible = visible;
        this.switchCompliment.visible = visible;
        this.switchDriverLogs.visible = visible;
        this.switchHOSData.visible = visible;
        this.switchImages.visible = visible;
        this.switchMobileDispatch.visible = visible;
        this.switchReeferTracking.visible = visible;
        this.switchTrailerTracking.visible = visible;
    }

    private validateVendorIdOnBlur(event: BlurEvent): void {
        const textbox = event.target as Textbox;
        const vendorId = textbox?.text?.trim();

        if (vendorId) {
            this.validateVendorId(textbox, vendorId);
        }
    }

    /**
     * Validates that the vendor ID starts with a known vendor code
     */
    private async validateVendorId(textbox: Textbox, vendorId: string): Promise<boolean> {
        const isTrackingEnabled = this.switchTrailerTracking.checked || this.switchReeferTracking.checked;
        return MobileCommValidationUtils.validateVendorId(
            textbox,
            vendorId,
            isTrackingEnabled,
            (vendorId) => this.updateSendLoadListenerTableVisibility(vendorId)
        );
    }

    /**
     * Updates the visibility of MobileCommControlSendLoadListenerTable based on vendorId
     */
    public updateSendLoadListenerTableVisibility(vendorId: string): void {
        const allowedVendors = ['KEEPTRUCK', 'SYMPHONY', 'SAMSARA'];
        const shouldBeVisible = allowedVendors.includes(vendorId.toUpperCase());

        if (this.owner?.tabSendLoadListener) {
            this.owner.tabSendLoadListener.visible = shouldBeVisible;
        }
    }

    private setupEventListeners(): void {
        // Set up data source listeners
        if (this.sourceMCControl) {
            this.sourceMCControl.addAfterModeChangeListener(this.onModeChange.bind(this));
        }
    }

    private onModeChange(event: DataSourceModeChangeEvent): void {
        const mode = event.newMode;

        switch (mode) {
            case DataSourceMode.ADD:
                // For whatever reason, the setting defaultDataValue in the layout did not workjjjjj
                this.switchActive.checked = true;
                break;
            case DataSourceMode.UPDATE:
                // Trigger existing logic to set tab visibility based on current switch values
                this.updateTabVisibilityForUpdateMode();
                break;
        }

        this.updateSwitchesVisibility();
    }

    public updateTabVisibilityForUpdateMode(): void {
        // Update trailer tab visibility based on tracking switches
        const isTrailerEnabled = this.switchTrailerTracking.checked;
        const isReeferEnabled = this.switchReeferTracking.checked;

        if (this.owner?.tabTrailer) {
            this.owner.tabTrailer.visible = isTrailerEnabled || isReeferEnabled;
        }

        const vendorId = this.textboxVendorId?.text?.trim();

        // Update ZMIT tab visibility based on ZMIT feature switches
        const shouldShowZmit = this.switchMobileDispatch.checked ||
                              this.switchCommunications.checked ||
                              this.switchCompliment.checked ||
                              vendorId?.startsWith("SYM");

        if (this.owner?.tabSendLoad) {
            this.owner.tabSendLoad.visible = shouldShowZmit;
        }
        if (this.owner?.tabSendLoadListener) {
            this.owner.tabSendLoadListener.visible = shouldShowZmit;
        }
        if (this.owner?.tabSendLoadOptions) {
            this.owner.tabSendLoadOptions.visible = shouldShowZmit && this.owner.showSendLoadOptonsTab();
        }
    }

    private setupUIEventListeners(): void {
        this.setupTrackingListeners();
        this.setupZmitFeatureListeners();
    }

    private addGeneralLayoutLoadListener(): void {
        this.addLayoutLoadListener(() => {
            this.textboxVendorId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
                if (this.mainDataSource.activeRow && this.owner?.buttonOpenPortal) {
                    this.owner.buttonOpenPortal.enabled = !StringUtil.isEmptyString(this.textboxVendorId.text);
                }
            });

            this.textboxVendorId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                const trailerTrackingValue = this.switchTrailerTracking.checked ? "Y" : "N";
                const reeferTrackingValue = this.switchReeferTracking.checked ? "Y" : "N";
                event.filter.trailer_tracking = trailerTrackingValue;
                event.filter.reefer_tracking = reeferTrackingValue;
            });
        });
    }

    async textboxVendorIdOnBlur(event: BlurEvent) {
        const isValid = await DuplicateId.validate(this.textboxVendorId, VALIDATION_CONFIG.DUPLICATE_VALIDATION_PROPS);
        if (isValid) {
            this.validateVendorIdOnBlur(event);
        }
        this.owner.tabContacts.visible = this.owner?.isSymphonyVendorType();
        this.updateSwitchesVisibility();
    }

    /**
     * Enforce maximum length and validate vendor ID field
     */
    textboxVendorIdOnChange(event: ChangeEvent): void {
        const textbox = event.target as Textbox;

        // Input validation and sanitization
        if (!textbox?.text) {
            return;
        }

        const finalText = this.sanitizeAndFormatVendorId(textbox.text);

        // Update the textbox if the text was modified
        if (finalText !== textbox.text) {
            textbox.text = finalText;
        }

        // Update portal button state
        this.updatePortalButtonState(finalText);

        // Hide all switches when the vendor is a Symphony ('SYM') vendor
        this.updateSwitchesVisibility();
    }

    private sanitizeAndFormatVendorId(input: string): string {
        // Sanitize input - only allow alphanumeric characters (no special characters)
        const sanitizedText = input.replace(/[^A-Za-z0-9]/g, '');

        // Convert to uppercase
        const uppercaseText = sanitizedText.toUpperCase();

        // Truncate text if it exceeds the maximum length
        if (uppercaseText.length > VALIDATION_CONFIG.VENDOR_ID_MAX_LENGTH) {
            return uppercaseText.substring(0, VALIDATION_CONFIG.VENDOR_ID_MAX_LENGTH);
        }

        return uppercaseText;
    }

    private updatePortalButtonState(vendorId: string): void {
        if (this.owner?.buttonOpenPortal) {
            this.owner.buttonOpenPortal.enabled = !StringUtil.isEmptyString(vendorId);
        }
    }

    private setupTrackingListeners(): void {
        const trackingManager = new TrackingListenerManager(this);
        trackingManager.setupListeners();
    }

    private setupZmitFeatureListeners(): void {
        const zmitManager = new ZmitFeatureManager(this);
        zmitManager.setupListeners();
    }
}

/**
 * Manages tracking-related event listeners and logic
 */
class TrackingListenerManager {
    constructor(private parent: MobileCommControlGeneral) {}

    setupListeners(): void {
        this.parent.switchTrailerTracking.addChangeListener(this.onTrailerTrackingChange.bind(this));
        this.parent.switchReeferTracking.addChangeListener(this.onReeferTrackingChange.bind(this));
    }

    private onTrailerTrackingChange(event: ChangeEvent): void {
        const isEnabled = event.newValue === 'Y' || event.newValue === true;
        const isReeferEnabled = this.parent.switchReeferTracking.checked;

        this.updateTrailerTabVisibility(isEnabled || isReeferEnabled);

        if (this.parent.mainDataSource.mode === DataSourceMode.ADD && event.userInitiatedChange && !isReeferEnabled) {
            this.resetVendorIdField();
        }
    }

    private onReeferTrackingChange(event: ChangeEvent): void {
        const isEnabled = event.newValue === 'Y' || event.newValue === true;
        const isTrailerEnabled = this.parent.switchTrailerTracking.checked;

        this.updateTrailerTabVisibility(isEnabled || isTrailerEnabled);

        if (this.parent.mainDataSource.mode === DataSourceMode.ADD && event.userInitiatedChange && !isTrailerEnabled) {
            this.resetVendorIdField();
        }
    }

    private updateTrailerTabVisibility(visible: boolean): void {
        if (this.parent.owner?.tabTrailer) {
            this.parent.owner.tabTrailer.visible = visible;
        }
    }

    private resetVendorIdField(): void {
        this.parent.textboxVendorId.focus();
        this.parent.textboxVendorId.clear();
        this.parent.activeRow.set("vendor_id", null);

        if (this.parent.owner?.buttonOpenPortal) {
            this.parent.owner.buttonOpenPortal.enabled = false;
        }
    }
}

/**
 * Manages ZMIT feature-related event listeners and logic
 */
class ZmitFeatureManager {
    constructor(private parent: MobileCommControlGeneral) {}

    setupListeners(): void {
        const zmitRelatedControls = [
            this.parent.switchMobileDispatch,
            this.parent.switchCommunications,
            this.parent.switchCompliment
        ];

        zmitRelatedControls.forEach(control => {
            control.addChangeListener(this.onZmitSectionToggle.bind(this));
        });
    }

    private onZmitSectionToggle(): void {
        const shouldShowZmit = this.isAnyZmitFeatureEnabled();

        if (this.parent.owner?.tabSendLoad) {
            this.parent.owner.tabSendLoad.visible = shouldShowZmit;
        }
        if (this.parent.owner?.tabSendLoadListener) {
            this.parent.owner.tabSendLoadListener.visible = shouldShowZmit;
        }
        if (this.parent.owner?.tabSendLoadOptions) {
            this.parent.owner.tabSendLoadOptions.visible = shouldShowZmit;
            this.parent.owner.tabSendLoadOptions.visible = shouldShowZmit && this.parent.owner.showSendLoadOptonsTab();
        }
    }

    private isAnyZmitFeatureEnabled(): boolean {
        // Update ZMIT tab visibility based on ZMIT feature switches
        return this.parent.switchMobileDispatch.checked ||
               this.parent.switchCommunications.checked ||
               this.parent.switchCompliment.checked ||
               this.parent.textboxVendorId?.text?.trim().startsWith("SYM");
    }
}
